import React from 'react';
import styled from '@emotion/native';
import { ConsultationStatus } from '@/shared/types/generated';
import { H3, BodyText } from '@/shared/components/StyledText';
import { green, mint, subheadline } from '@/shared/styles/colors';
import Button from '@/shared/components/Button';
import { BOX_SHADOW_STYLE } from '@/shared/styles/constants';
import { Href, useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import useStore from '@/stores/useStore';

interface EmptySlotProps {
  status: ConsultationStatus;
}

const getMessageForStatus = (
  status: ConsultationStatus,
  isStylist: boolean,
): { title: string; body: string; buttonText: string; buttonRoute?: Href } => {
  switch (status) {
    case ConsultationStatus.Pending:
      return {
        title: 'No Pending Requests',
        body: "When clients request in-person sessions, they'll appear here for your review.",
        buttonText: 'Manage Availability',
        buttonRoute: ROUTES.AVAILABILITY.path,
      };
    case ConsultationStatus.Upcoming:
      return {
        title: isStylist
          ? 'Ready to Get Booked?'
          : 'your calendar is wide open',
        body: isStylist
          ? 'Add your availability to start connecting with clients.'
          : 'No sessions booked yet',
        buttonText: isStylist ? 'Manage Availability' : 'Book one now',
        buttonRoute: isStylist ? ROUTES.AVAILABILITY.path : ROUTES.EXPLORE.path,
      };
    case ConsultationStatus.Completed:
      return {
        title: 'Nothing Here (Yet!)',
        body: "Once you finish a session,\nyou'll find it here.",
        buttonText: 'Explore Stylists',
        buttonRoute: isStylist ? undefined : ROUTES.EXPLORE.path,
      };
    case ConsultationStatus.Cancelled:
      return {
        title: isStylist
          ? 'No Canceled Sessions'
          : 'All Clear— \nNo Canceled Sessions',
        body: isStylist
          ? "No canceled sessions to display. If a client cancels a session, you'll see it here."
          : "Any canceled sessions will be \nlisted here. Whenever you're ready, \nyou can book again.",
        buttonText: 'explore stylists',
        buttonRoute: isStylist ? undefined : ROUTES.EXPLORE.path,
      };
    default:
      return {
        title: 'No Sessions',
        body: 'No sessions to display.',
        buttonText: 'explore stylists',
        buttonRoute: ROUTES.EXPLORE.path,
      };
  }
};

export const EmptySlot: React.FC<EmptySlotProps> = ({ status }) => {
  const { isStylist } = useStore();
  const router = useRouter();
  const message = getMessageForStatus(status, isStylist ?? false);

  return (
    <Container>
      <StyledH3>{message.title}</StyledH3>
      <StyledBodyText>{message.body}</StyledBodyText>
      {message.buttonRoute && (
        <StyledButton
          variant="primary"
          text={message.buttonText}
          onPress={() => {
            if (message.buttonRoute) {
              router.push(message.buttonRoute);
            }
          }}
          customStyle={{ width: 263 }}
        />
      )}
    </Container>
  );
};

const Container = styled.View({
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  backgroundColor: mint,
  paddingVertical: 100,
  borderRadius: 12,
  ...BOX_SHADOW_STYLE,
  flexGrow: 1,
});

const StyledH3 = styled(H3)({
  textAlign: 'center',
  textTransform: 'uppercase',
  color: green,
  marginBottom: 10,
  marginHorizontal: 40,
});

const StyledBodyText = styled(BodyText)({
  textAlign: 'center',
  color: subheadline,
  marginBottom: 21,
  marginHorizontal: 23,
});

const StyledButton = styled(Button)({
  width: 263,
  marginTop: 8,
});

export default EmptySlot;
